import React, { useCallback, useState } from 'react';
import { Icon } from '@iconify/react';
import { CtyButton, CtyShowToast, checkType } from '@circuitry-ai/doc-ui';
import { useTranslation } from 'react-i18next';

/** Depth up to which nodes are expanded by default (root + first level). */
const DEFAULT_EXPAND_DEPTH = 1;
const INDENT_PX = 14;

const isExpandable = (value: unknown): value is Record<string, unknown> | unknown[] =>
    value !== null && typeof value === 'object';

const nodeSummary = (value: unknown): string => {
    if (Array.isArray(value)) return `[${value.length}]`;
    if (isExpandable(value)) return `{${Object.keys(value).length}}`;
    return '';
};

const toEntries = (value: Record<string, unknown> | unknown[]): Array<[string | number, unknown]> =>
    Array.isArray(value) ? value.map((item, index) => [index, item]) : Object.entries(value);

const CopyButton: React.FC<{ value: unknown; ariaLabel: string }> = ({ value, ariaLabel }) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(typeof value === 'string' ? value : JSON.stringify(value, null, 2));
            setCopied(true);
            CtyShowToast(checkType.success, t('Copied to clipboard'));
            setTimeout(() => setCopied(false), 2000);
        } catch {
            CtyShowToast(checkType.error, t('Failed to copy'));
        }
    }, [value, t]);

    return (
        <CtyButton
            isIconOnly variant="light" size="sm" aria-label={ariaLabel} onPress={handleCopy}
            className="min-w-6 w-6 h-6 rounded-md data-[hover=true]:bg-default-200"
        >
            <Icon icon={copied ? 'mdi:check-circle' : 'hugeicons:copy-01'} height={14} width={14} className={copied ? 'text-success' : 'text-default-500'} />
        </CtyButton>
    );
};

const PrimitiveValue: React.FC<{ value: unknown }> = ({ value }) => {
    if (value === null || value === undefined) return <span className="text-default-400">null</span>;
    if (typeof value === 'string') return <span className="text-success break-all">&quot;{value}&quot;</span>;
    if (typeof value === 'number') return <span className="text-primary">{String(value)}</span>;
    if (typeof value === 'boolean') return <span className="text-secondary">{String(value)}</span>;
    return <span className="text-foreground break-all">{String(value as string | number | bigint | boolean | symbol)}</span>;
};

const JsonNode: React.FC<{ nodeKey?: string | number; value: unknown; depth: number }> = ({ nodeKey, value, depth }) => {
    const [expanded, setExpanded] = useState(depth < DEFAULT_EXPAND_DEPTH);
    const indentStyle = { paddingLeft: depth * INDENT_PX };

    const keyLabel = nodeKey === undefined ? null : (
        <>
            <span className="text-default-600 font-medium">{String(nodeKey)}</span>
            <span className="text-default-400">:</span>
        </>
    );

    if (!isExpandable(value)) {
        return (
            <div className="flex items-start gap-1 py-0.5" style={indentStyle}>
                {keyLabel}
                <PrimitiveValue value={value} />
            </div>
        );
    }

    const bracket = Array.isArray(value) ? ['[', ']'] : ['{', '}'];

    return (
        <div className="min-w-0">
            <div className="group flex items-center gap-1 py-0.5" style={indentStyle}>
                <button
                    type="button" onClick={() => setExpanded((prev) => !prev)} aria-label={expanded ? 'Collapse' : 'Expand'}
                    className="flex items-center justify-center w-4 h-4 shrink-0 text-default-500 hover:text-foreground"
                >
                    <Icon icon={expanded ? 'mdi:chevron-down' : 'mdi:chevron-right'} width={16} height={16} />
                </button>
                {keyLabel}
                <span className="text-default-500">
                    {expanded ? bracket[0] : `${bracket[0]}${nodeSummary(value)}${bracket[1]}`}
                </span>
                <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <CopyButton value={value} ariaLabel="Copy value" />
                </span>
            </div>
            {expanded ? (
                <>
                    {toEntries(value).map(([childKey, childValue]) => (
                        <JsonNode key={String(childKey)} nodeKey={childKey} value={childValue} depth={depth + 1} />
                    ))}
                    <div className="text-default-500 py-0.5" style={indentStyle}>
                        {bracket[1]}
                    </div>
                </>
            ) : null}
        </div>
    );
};

export interface JsonTreeViewProps {
    readonly data: unknown;
    readonly className?: string;
}

/** Collapsible JSON tree viewer: every object/array node expands/collapses independently and can be copied. */
export const JsonTreeView: React.FC<JsonTreeViewProps> = ({ data, className }) => {
    const { t } = useTranslation();

    return (
        <div className={`rounded-md border border-divider bg-content2 ${className ?? ''}`}>
            <div className="flex items-center justify-between px-2 py-1 border-b border-divider">
                <span className="text-xs font-medium text-default-500">JSON</span>
                <CopyButton value={data} ariaLabel={t('Copy JSON')} />
            </div>
            <div className="p-2 overflow-x-auto font-mono text-xs leading-relaxed text-foreground">
                {isExpandable(data) ? <JsonNode value={data} depth={0} /> : <PrimitiveValue value={data} />}
            </div>
        </div>
    );
};

export default JsonTreeView;
